import cn from "classnames";
import DropdownBtn from "components/shared/dropdown";
import Select from "components/shared/select";
import format from "date-fns/format";
import ru from "date-fns/locale/ru";
import sub from "date-fns/sub";
import { humanizedTimeRange } from "helpers/date";
import { organizationActivityPeriodTypes, organizationActivityPeriodTypesEnum } from "helpers/enums";
import { viewContainer } from "helpers/redux";
import { enumLabel, formatNumber, hasValue } from "helpers/system";
import upperFirst from "lodash/upperFirst";
import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { list as logistsList } from "sources/books/employees";
import { list as transportColumnsList } from "sources/books/transportColumns";
import { getData } from "sources/dashboard";
import Goals from "./goals";
// import Recommendations from "./recommendations";
import Settings from "./settings";

const Chart = lazy(() => import("./chart.js"));

const dashboardPeriodFormat = {
    [organizationActivityPeriodTypesEnum.Months]: "LLLL yyyy",
    [organizationActivityPeriodTypesEnum.Quarters]: "QQQQ yyyy",
    [organizationActivityPeriodTypesEnum.Years]: "yyyy",
};

const tableRows = (currency, withoutVat) => {
    const vatSuffix = withoutVat ? " (без НДС)" : "";

    return [
        { key: "countOfCars", name: "Количество ТС" },
        { key: "averageMileage", name: "Средний пробег на ТС" },
        {
            key: withoutVat ? "averageBidPriceWithoutVat" : "averageBidPrice",
            targetValueKey: "averageBidPrice",
            name: `Вал на ТС${vatSuffix}`,
        },
        {
            key: withoutVat ? "outputPerKmWithoutVat" : "outputPerKm",
            targetValueKey: "outputPerKm",
            name: `${upperFirst(currency)}/км${vatSuffix}`,
        },
        {
            key: withoutVat ? "outputPerHourWithoutVat" : "outputPerHour",
            targetValueKey: "outputPerHour",
            name: `${upperFirst(currency)}/час${vatSuffix}`,
        },
        { key: "emptyMileageRate", name: "% Порожнего пробега", reversed: true },
        {
            key: "averageDowntime",
            name: "Средний ПРР на ТС",
            formatter: (val) => humanizedTimeRange(val),
            reversed: true,
        },
    ];
};

const calculateTargetPercent = (value, targetValue) => {
    if (!hasValue(value) || !hasValue(targetValue)) {
        return undefined;
    }

    const numberValue = Number(value);
    const numberTargetValue = Number(targetValue);

    if (!Number.isFinite(numberValue) || !Number.isFinite(numberTargetValue) || numberTargetValue === 0) {
        return undefined;
    }

    return Math.round((numberValue * 100) / numberTargetValue);
};

const getChartTargetPercent = (row, item, targetRow) => {
    const targetPercent = row[`${item.key}TargetPercent`];
    if (hasValue(targetPercent)) {
        return targetPercent;
    }

    return calculateTargetPercent(row[item.key], targetRow?.[item.targetValueKey]);
};

const Dashboard = (props) => {
    const [loading, setLoading] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [data, setData] = useState();
    const [withoutVat, setWithoutVat] = useState(false);
    const [filters, setFilters] = useState({
        startDate: sub(new Date(), { months: 3 }),
        endDate: new Date(),
        periodType: organizationActivityPeriodTypesEnum.Months,
    });
    const rows = useMemo(
        () => tableRows(props.organization?.currencyData?.name, withoutVat),
        [props.organization?.currencyData?.name, withoutVat],
    );

    const formatRow = (row) => {
        const periodType = data.data[0].periodType;
        const month =
            periodType === organizationActivityPeriodTypesEnum.Months
                ? row.month
                : periodType === organizationActivityPeriodTypesEnum.Quarters
                  ? 3 * row.quarter
                  : 1;
        return format(new Date(row.year, month, 0), dashboardPeriodFormat[periodType], { locale: ru });
    };

    const refreshData = () => {
        setLoading(true);
        getData(filters).then((data) => {
            setData(data);
            setLoading(false);
            setLoaded(true);
        });
    };

    useEffect(() => {
        refreshData();
    }, []);

    const showGoalModal = () => {
        props.modal({
            name: "default",
            windowMod: "wide",
            withoutHeader: true,
            CustomBody: Goals,
            bodyData: { organization: props.organization },
        });
    };

    // const showRecommendationsModal = () => {
    //     props.modal({
    //         name: "default",
    //         title: "Рекомендации по достижению цели",
    //         windowMod: "wide",
    //         withoutHeader: true,
    //         CustomBody: Recommendations,
    //     });
    // };

    const showSettings = () => {
        props.modal({
            name: "default",
            windowMod: "wide",
            withoutHeader: true,
            CustomBody: Settings,
        });
    };

    const dashboardPeriod = useMemo(() => {
        return (
            <div className="inline-btn inline-btn--without-icon">
                {filters.startDate ? format(filters.startDate, "LLLL, yyyy", { locale: ru }) : ""}&nbsp;&mdash;&nbsp;
                {filters.endDate ? format(filters.endDate, "LLLL, yyyy", { locale: ru }) : ""}
                <i className="icon-uEA61-angle-down pl-1 inline-icon" />
            </div>
        );
    }, [filters.startDate, filters.endDate]);

    const chartData = useMemo(() => {
        if (!data) {
            return [{}];
        }

        const targetRow = data.data[data.data.length - 1];

        return rows.reduce((acc, item) => {
            const itemRes = { name: item.name };
            data.data.forEach((row) => {
                if (row.index != null) {
                    const key = row.index === 0 ? "Стартовые показатели" : upperFirst(formatRow(row));
                    itemRes[key] = getChartTargetPercent(row, item, targetRow);
                }
            });
            acc.push(itemRes);
            return acc;
        }, []);
    }, [data?.data, rows]);

    const chartDataKeys = data?.data
        ? ["Стартовые показатели"].concat(data.data.slice(1, -1).map((item) => upperFirst(formatRow(item))))
        : [];

    const changePercent = (value, reversed) => {
        if (!value) {
            return null;
        }
        return (
            <small
                className={cn("pl-2", {
                    "text-success": reversed ? value < 0 : value > 0,
                    "text-danger": reversed ? value > 0 : value < 0,
                })}
            >
                <i
                    className={cn("inline-icon pr-1", {
                        "icon-uEABD-circle-up": value > 0,
                        "icon-uEABE-circle-down": value < 0,
                    })}
                />
                {value} %
            </small>
        );
    };

    const periodTypeItems = useMemo(() => {
        return organizationActivityPeriodTypes.map((item) => ({
            text: item.label,
            action: () => setFilters({ ...filters, periodType: item.value }),
            show: true,
        }));
    }, []);

    const { isTruckingOrganizationAdmin } = props.roles;

    const toggleWithoutVat = () => setWithoutVat((value) => !value);

    return (
        <div className={cn("page-content", { "loader-snippet--disable-loader": loading })}>
            <div className="page-col page-col--full mounted">
                <div className={cn("page-book__list d-flex flex-column", { loading })}>
                    <div className="book-list__head">
                        <div className="book-list__head-close close close--sm" />
                        <div className="book-list__head-title">Дашборд</div>
                        <div>
                            <DropdownBtn
                                withoutChevron
                                wrapCname="font-size-14"
                                text={
                                    <>
                                        {enumLabel(organizationActivityPeriodTypes, filters.periodType)}
                                        <i className="icon-uEA61-angle-down pl-1 inline-icon" />
                                    </>
                                }
                                cname="inline-btn inline-btn--without-icon mr-0"
                                items={periodTypeItems}
                            />
                            <div className="react-datepicker--reset d-inline-block mx-2">
                                <ReactDatePicker
                                    customInput={dashboardPeriod}
                                    selected={filters.startDate}
                                    onChange={([startDate, endDate]) => setFilters({ ...filters, startDate, endDate })}
                                    selectsRange
                                    startDate={filters.startDate}
                                    endDate={filters.endDate}
                                    maxDate={new Date()}
                                    locale={ru}
                                    dateFormat="MM/yyyy"
                                    showMonthYearPicker
                                />
                            </div>
                            <div className="inline-btn inline-btn--without-icon" onClick={refreshData}>
                                <i className="icon-uEA18-update icon-inline pr-1" />
                                Обновить
                            </div>
                        </div>
                    </div>
                    <div className="container-fluid">
                        <div className="pb-3 align-items-center d-flex w-100">
                            <div className="flex-grow-1 dashboard__filters pr-2">
                                <Select
                                    inputProps={{ name: "logistId" }}
                                    propName="fullNameOrEmail"
                                    url={logistsList}
                                    placeholder="Все логисты"
                                    value={filters.logistId}
                                    onChange={({ name, value }) => setFilters({ ...filters, [name]: value })}
                                />
                                <Select
                                    inputProps={{ name: "transportColumnId" }}
                                    placeholder="Все колонны"
                                    url={transportColumnsList}
                                    propName="name"
                                    value={filters.transportColumnId}
                                    onChange={({ name, value }) => setFilters({ ...filters, [name]: value })}
                                />
                            </div>
                            <div
                                className="d-flex align-items-center font-size-14 cursor-pointer mr-3"
                                onClick={toggleWithoutVat}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        toggleWithoutVat();
                                    }
                                }}
                                role="checkbox"
                                tabIndex={0}
                                aria-checked={withoutVat}
                            >
                                <span
                                    className={cn("checkbox d-inline-block mr-2", {
                                        "checkbox--checked": withoutVat,
                                    })}
                                />
                                <span className="checkbox__text">Без НДС</span>
                            </div>
                            {/* <div className="btn btn-sm btn-brand float-right mr-2" onClick={showRecommendationsModal}>
                                Рекомендации
                            </div> */}
                            {isTruckingOrganizationAdmin && (
                                <>
                                    <div className="btn btn-sm btn-secondary" onClick={showGoalModal}>
                                        <i className="icon-uEA10-pen inline-icon pr-1" />
                                        Изменить цели
                                    </div>
                                    <div className="btn btn-sm btn-secondary ml-1" onClick={showSettings}>
                                        <i className="icon-uEA0C-gear inline-icon pr-1" />
                                        Настройки
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="flex-grow-1 container-fluid dashboard overflow-auto">
                        {loaded && (
                            <div className="card card-body">
                                <Suspense fallback={<div className="loading" />}>
                                    <Chart chartData={chartData} chartDataKeys={chartDataKeys} />
                                </Suspense>
                                <table className="table table-sm mt-3 table-responsive-sm">
                                    <thead>
                                        <tr>
                                            <th>Показатель</th>
                                            <th>Стартовые показатели</th>
                                            {data?.data.slice(1, -1).map((item, i) => (
                                                <th key={i}>{upperFirst(formatRow(item))}</th>
                                            ))}
                                            <th>Цель</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map(({ key, name, formatter, reversed }) => (
                                            <tr key={key}>
                                                <td>{name}</td>
                                                {data?.data.map((item, i) => (
                                                    <td key={i}>
                                                        {formatter ? formatter(item[key]) : formatNumber(item[key])}
                                                        {changePercent(item[`${key}ChangePercent`], reversed)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default viewContainer(Dashboard);
